// RunFlowGroups — what went into a run and what came out, by week, day and
// variety (linked runs phase 4; T2). Read-only. A bulk sort's output is its
// bulk-sort pallets; a packing run's is its pallet lines. On a one-variety,
// one-day run this is a single line.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CalendarRange, ChevronDown, ChevronRight } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { readState } from '@/lib/readState'
import { fmtKg } from '@/lib/runsKpis'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { fmtDateZA } from '@/lib/dates'
import { groupRunFlow, type FlowIn, type FlowOut, type WeekFlow } from '@/lib/runFlowGroups'

interface TipRow {
  tipped_at: string; bin_quantity: number; net_weight_kg: number | string | null
  deliveries: { variety: string | null } | null
  bins: { variety: string | null } | null
  cooling_batches: { variety: string | null } | null
}

/** A pallet line's net, or a bulk-sort pallet's net still on it plus its tipped portions. */
function madeKg(o: Record<string, unknown>): number {
  if (o.net_mass_kg !== undefined) return Number(o.net_mass_kg ?? 0)
  const tips = (o.tipping_log as { net_weight_kg: number | string | null }[] | null) ?? []
  const tipped = tips.reduce((s, t) => s + Number(t.net_weight_kg ?? 0), 0)
  return (o.status === 'TIPPED' ? 0 : Number(o.net_weight_kg ?? 0)) + tipped
}

function useRunFlow(run: { run_id: string; run_kind?: string | null; variety: string | null }) {
  const tenantId = useActiveTenantId()
  return useQuery({
    queryKey: ['run-flow-groups', tenantId, run.run_id],
    queryFn: async () => {
      // Paged (fetchAllPaged), so a long bulk sort's hundreds of tips are never cut at 1,000.
      const [tips, outs] = await Promise.all([
        fetchAllPaged<TipRow>((lo, hi) => supabase.from('tipping_log')
          .select('tipping_id, tipped_at, bin_quantity, net_weight_kg, deliveries:delivery_id(variety), bins:bin_id(variety), cooling_batches:batch_id(variety)')
          .eq('run_id', run.run_id)
          .order('tipped_at').order('tipping_id')
          .range(lo, hi) as unknown as PromiseLike<{ data: TipRow[] | null; error: { message: string } | null }>),
        run.run_kind === 'BULK_SORT'
          // A bulk-sort pallet's made kg is what is still on it plus every
          // portion tipped off it (retip_bin lowers its net per portion, #4114
          // review). A cancelled pallet made nothing.
          ? fetchAllPaged<Record<string, unknown>>((lo, hi) => supabase.from('bins')
              .select('bin_id, created_at, variety, crate_count, net_weight_kg, status, tipping_log(net_weight_kg)')
              .eq('source_run_id', run.run_id).is('deleted_at', null).neq('status', 'CANCELLED')
              .order('created_at').order('bin_id')
              .range(lo, hi) as unknown as PromiseLike<{ data: Record<string, unknown>[] | null; error: { message: string } | null }>)
          : fetchAllPaged<Record<string, unknown>>((lo, hi) => supabase.from('pallet_lines')
              .select('line_id, created_at, variety, carton_count, net_mass_kg')
              .eq('source_run_id', run.run_id).is('deleted_at', null)
              .order('created_at').order('line_id')
              .range(lo, hi) as unknown as PromiseLike<{ data: Record<string, unknown>[] | null; error: { message: string } | null }>),
      ])
      if (tips.error) throw new Error(tips.error)
      if (outs.error) throw new Error(outs.error)
      const ins: FlowIn[] = tips.rows.map(t => ({
        at: t.tipped_at,
        variety: t.deliveries?.variety ?? t.bins?.variety ?? t.cooling_batches?.variety ?? null,
        count: Number(t.bin_quantity ?? 0), kg: Number(t.net_weight_kg ?? 0),
      }))
      const made: FlowOut[] = outs.rows.map(o => ({
        at: String(o.created_at),
        variety: (o.variety as string | null) ?? null,
        count: Number(o.crate_count ?? o.carton_count ?? 0),
        kg: madeKg(o),
      }))
      return groupRunFlow(ins, made, run.variety)
    },
  })
}

const kg = fmtKg

export default function RunFlowGroups({ run }: { run: { run_id: string; run_kind?: string | null; variety: string | null } }) {
  const q = useRunFlow(run)
  const state = readState(q)
  if (state === 'loading') return null
  if (state === 'unread') {
    return <p className="card text-caption text-fg-muted">Couldn&apos;t read this run by week, day and variety. Check the connection and open the run again.</p>
  }
  const weeks = q.data ?? []
  // A run of one day and one variety says nothing the totals don't.
  if (weeks.length === 0) return null
  if (weeks.length === 1 && weeks[0].days.length === 1 && weeks[0].days[0].varieties.length === 1) return null
  const made = run.run_kind === 'BULK_SORT' ? 'Bulk-sort pallets' : 'Packed'
  return (
    <div className="card space-y-2">
      <p className="text-caption text-fg-muted flex items-center gap-1.5"><CalendarRange size={14} /> By week, day and variety</p>
      {weeks.map((w, i) => <WeekBlock key={w.weekStart} week={w} made={made} startOpen={i === 0} />)}
    </div>
  )
}

function WeekBlock({ week, made, startOpen }: { week: WeekFlow; made: string; startOpen: boolean }) {
  const [open, setOpen] = useState(startOpen)
  return (
    <div className="border-t hairline pt-2">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        className="tap-target-y w-full flex items-center justify-between text-sm font-semibold text-fg">
        <span className="flex items-center gap-1">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Week of {fmtDateZA(week.weekStart)}
        </span>
        <span className="text-caption text-fg-muted tabular-nums">in {kg(week.inKg)} · {made.toLowerCase()} {kg(week.outKg)}</span>
      </button>
      {open && week.days.map(d => (
        <div key={d.day} className="mt-1.5 pl-5">
          <p className="text-caption font-semibold text-fg-secondary">{fmtDateZA(d.day)}</p>
          <table className="w-full text-caption tabular-nums">
            <thead>
              <tr className="text-fg-muted">
                <th className="text-left font-normal">Variety</th>
                <th className="text-right font-normal">In</th>
                <th className="text-right font-normal">{made}</th>
              </tr>
            </thead>
            <tbody>
              {d.varieties.map(v => (
                <tr key={v.variety}>
                  <td className="text-left text-fg-secondary">{v.variety}</td>
                  <td className="text-right">{v.inCount} · {kg(v.inKg)}</td>
                  <td className="text-right">{v.outCount} · {kg(v.outKg)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}
